import { Navigate, Outlet, useLocation } from 'react-router'
import type { JSX } from 'react'
import { ErrorState, FullPageLoading } from '@/ui/states'
import { TrialEnded } from './TrialEnded'
import { useTenants } from './context'

/**
 * Paths that keep working after the trial lapses, mirroring `UNGATED_PREFIXES`
 * in `api/app/tenancy/dependencies.py` (AUTH-19).
 *
 * A mirror, not a source of truth — the API refuses regardless — and it is much
 * shorter than the server's list on purpose: this only has to name the screens a
 * lapsed tenant is *sent to*, not every route that keeps answering. Billing is
 * the way out; the rest of the shell is replaced.
 */
const UNGATED_PATHS = ['/billing', '/organisations', '/settings/billing']

/**
 * Everything inside the shell needs an organisation selected on the session.
 *
 * Three outcomes, and each of them is a real state rather than a fallback:
 *
 *  * still loading the list — the whole frame waits, because rendering a
 *    sidebar with no organisation name and then filling it in is a flash;
 *  * the list could not be read — a full-page error with the request id,
 *    since without it there is no shell to show the failure *inside*;
 *  * no organisation selected — `/organisations`, which is where both a brand
 *    new account and someone removed from their last one belong.
 */
export function RequireTenant(): JSX.Element {
  const { status, failure, active, reload } = useTenants()
  const { pathname } = useLocation()

  if (status === 'loading') return <FullPageLoading label="Loading your organisations" />
  if (status === 'error' && failure) {
    return (
      <div className="app-full-state">
        <ErrorState failure={failure} title="Could not load your organisations" onRetry={reload} />
      </div>
    )
  }
  if (!active) return <Navigate to="/organisations" replace />
  // AUTH-19. The whole shell is replaced rather than each screen reporting its
  // own 402: a sidebar full of links that all refuse is eleven dead ends and one
  // unanswered question. `TrialEnded` answers it once, and the paths that lead
  // out of it are the ones the server also keeps open.
  if (active.tenant.access === 'lapsed' && !isUngated(pathname)) return <TrialEnded />
  return <Outlet />
}

function isUngated(pathname: string): boolean {
  return UNGATED_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`))
}
